/**
 * Download an image robustly across desktop, iOS Safari, Android
 * Chrome, and — critically for the Chinese market — WeChat / QQ /
 * Weibo in-app browsers where the `<a download>` attribute is
 * ignored.
 *
 * Strategy, in order:
 *   1. If the input is a `data:` URL, decode it to a Blob directly
 *      (no fetch — some in-app browsers block fetch on data: URLs).
 *   2. If `navigator.canShare({ files })` is supported, use the
 *      native Share Sheet — this is the ONLY reliable "Save to
 *      Photos" path on iOS Safari and in most Chinese webviews.
 *   3. Otherwise, create a Blob URL and click a hidden anchor with
 *      the `download` attribute (works on all desktop browsers +
 *      Android Chrome).
 *   4. If we detect the environment silently ignored the download
 *      attribute (iOS Safari, WeChat WebView), open the Blob URL in
 *      a new tab and tell the user to long-press to save. This is
 *      the last-resort path that will never fail silently.
 *
 * @param {string} src        `data:` URL or blob/http URL of the image.
 * @param {string} filename   Suggested filename with extension.
 * @returns {Promise<{ok: boolean, method: string, hint?: string}>}
 */
export async function downloadImage(src, filename) {
    if (!src) {
        return { ok: false, method: "none", hint: "no image" };
    }

    // ---- 1. Materialise a Blob ----
    let blob;
    let mime = "image/png";
    try {
        if (src.startsWith("data:")) {
            const m = src.match(/^data:(.+?);base64,(.*)$/s);
            if (!m) throw new Error("bad data URL");
            mime = m[1];
            const binary = atob(m[2]);
            const bytes = new Uint8Array(binary.length);
            for (let i = 0; i < binary.length; i++) {
                bytes[i] = binary.charCodeAt(i);
            }
            blob = new Blob([bytes], { type: mime });
        } else {
            const res = await fetch(src);
            if (!res.ok) throw new Error(`fetch ${res.status}`);
            blob = await res.blob();
            mime = blob.type || mime;
        }
    } catch (err) {
        return { ok: false, method: "decode_failed", hint: String(err) };
    }

    // ---- 2. Native Share Sheet (best on iOS + Chinese webviews) ----
    try {
        const ext = filename.split(".").pop() || "png";
        const file = new File([blob], filename, {
            type: mime || `image/${ext}`,
        });
        if (
            typeof navigator !== "undefined" &&
            navigator.canShare &&
            navigator.canShare({ files: [file] }) &&
            typeof navigator.share === "function"
        ) {
            await navigator.share({ files: [file], title: filename });
            return { ok: true, method: "web_share" };
        }
    } catch (err) {
        // User cancelled or share failed — fall through to anchor.
        if (err && err.name === "AbortError") {
            return { ok: true, method: "web_share_cancelled" };
        }
    }

    // ---- 3. Anchor with download attribute (works desktop + Android) ----
    const url = URL.createObjectURL(blob);
    const supportsDownload = "download" in document.createElement("a");
    if (supportsDownload) {
        try {
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            a.rel = "noopener";
            a.style.display = "none";
            document.body.appendChild(a);
            a.click();
            a.remove();
            // Revoke after a tick so mobile browsers finish the write.
            setTimeout(() => URL.revokeObjectURL(url), 4000);
            if (!isRestrictedWebView()) {
                return { ok: true, method: "anchor_download" };
            }
            // WeChat / QQ etc. will have silently ignored the click;
            // continue to the fallback below.
        } catch (_err) {
            // ignore, try last-resort
        }
    }

    // ---- 4. Last resort: open in new tab, tell user to long-press ----
    try {
        window.open(url, "_blank", "noopener");
        return {
            ok: true,
            method: "new_tab",
            hint: "long_press_to_save",
        };
    } catch (err) {
        URL.revokeObjectURL(url);
        return { ok: false, method: "new_tab_blocked", hint: String(err) };
    }
}

function isRestrictedWebView() {
    if (typeof navigator === "undefined") return false;
    const ua = navigator.userAgent || "";
    return (
        /MicroMessenger/i.test(ua) || // WeChat
        /QQ\//i.test(ua) ||           // Mobile QQ
        /QQBrowser/i.test(ua) ||
        /Weibo/i.test(ua) ||
        /Instagram/i.test(ua) ||
        // iOS Safari ignores `download` on cross-origin AND same-origin
        // blob: URLs in some versions.
        (/iPad|iPhone|iPod/.test(ua) && !/CriOS|FxiOS/.test(ua))
    );
}
